import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    IEPDashboardFilter,
    IEPDistributionDto,
    IEPYearTotalDto,
    IEPGrandTotalDto,
} from 'generated/openApi';
import ApiService from 'services/apiService';
import { useDashboardSchoolYear } from './DashboardSchoolYearContext';

interface IEPDataContextType {
    data: IEPDistributionDto[];
    loading: boolean;
    error: string | null;
    filter: IEPDashboardFilter;
    activeSchoolYear: number | null;
    totalsByYear: IEPYearTotalDto[];
    grandTotal: IEPGrandTotalDto | null;
    updateFilter: (filter: Partial<IEPDashboardFilter>) => void;
    refetch: () => void;
}

const IEPDataContext = createContext<IEPDataContextType | undefined>(undefined);

export const IEPDataProvider = ({ children }: { children: React.ReactNode }) => {
    const { schoolYear } = useDashboardSchoolYear();
    const [data, setData] = useState<IEPDistributionDto[]>([]);
    const [totalsByYear, setTotalsByYear] = useState<IEPYearTotalDto[]>([]);
    const [grandTotal, setGrandTotal] = useState<IEPGrandTotalDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeSchoolYear, setActiveSchoolYear] = useState<number | null>(null);
    const [filter, setFilter] = useState<IEPDashboardFilter>({
        schoolYears: [],
    });
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (schoolYear) {
            setInitialized(true);
            setFilter(prev => ({ ...prev, schoolYears: [schoolYear] }));
        }
    }, [schoolYear]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const responseData = await ApiService.iepDashboard.getIEPDistribution(filter);

            if (responseData.activeSchoolYear && !activeSchoolYear) {
                setActiveSchoolYear(responseData.activeSchoolYear);

                if (!initialized && filter.schoolYears.length === 0) {
                    setFilter(prev => ({
                        ...prev,
                        schoolYears: [responseData.activeSchoolYear]
                    }));
                    setInitialized(true);
                    return;
                }
            }

            setData(responseData.data || []);
            setTotalsByYear(responseData.totalsByYear || []);
            setGrandTotal(responseData.grandTotal || null);
        } catch (err) {
            setError('Failed to load IEP data');
            console.error('Error fetching IEP data:', err);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (initialized) {
            fetchData();
        }
    }, [filter]);

    const updateFilter = (updates: Partial<IEPDashboardFilter>) => {
        setFilter(prev => ({ ...prev, ...updates }));
    };

    const refetch = () => {
        fetchData();
    };

    return (
        <IEPDataContext.Provider
            value={{
                data,
                loading,
                error,
                filter,
                activeSchoolYear,
                totalsByYear,
                grandTotal,
                updateFilter,
                refetch,
            }}
        >
            {children}
        </IEPDataContext.Provider>
    );
};

export const useIEPData = () => {
    const context = useContext(IEPDataContext);
    if (!context) {
        throw new Error('useIEPData must be used within IEPDataProvider');
    }
    return context;
};
